import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Instagram,
  Search,
  RefreshCw,
  MessageSquare,
  Send,
  CheckCheck,
  AlertCircle,
  X,
  Loader2,
  ArrowLeft,
  ExternalLink,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ApiService from '../../services/ApiService';
import toast from 'react-hot-toast';

// ─── Date helpers ─────────────────────────────────────────────────────────────

const parseDateAsUTC = (dateStr) => {
  if (!dateStr) return null;
  let s = dateStr;
  if (s.includes(' ') && !s.includes('T')) s = s.replace(' ', 'T');
  if (!s.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(s)) s += 'Z';
  return new Date(s);
};

const formatTime = (isoString) => {
  if (!isoString) return '';
  try {
    const d = parseDateAsUTC(isoString);
    if (!d || isNaN(d.getTime())) return '';
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMin / 60);
    const diffDays = Math.floor(diffHours / 24);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return ''; }
};

const formatMsgTime = (isoString) => {
  if (!isoString) return '';
  try {
    const d = parseDateAsUTC(isoString);
    if (!d || isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

const getMessageDateLabel = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = parseDateAsUTC(dateStr);
    if (!d || isNaN(d.getTime())) return '';
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return 'Today';
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  } catch { return ''; }
};

// ─── Avatar helpers ───────────────────────────────────────────────────────────

const GRADIENTS = [
  'from-pink-400 to-rose-500',
  'from-violet-400 to-purple-600',
  'from-amber-400 to-orange-500',
  'from-cyan-400 to-teal-500',
  'from-green-400 to-emerald-500',
  'from-blue-400 to-indigo-500',
];

const getGradient = (id) => {
  if (!id) return GRADIENTS[0];
  const hash = [...id].reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return GRADIENTS[hash % GRADIENTS.length];
};

const getInitials = (contact) => {
  const name = contact?.metadata?.name || contact?.metadata?.username || contact?.platform_user_id || '?';
  return name.slice(0, 2).toUpperCase();
};

// ─── Platform config (easily extensible for WhatsApp, Messenger, etc.) ────────

const PLATFORMS = {
  instagram: {
    key: 'instagram',
    label: 'Instagram',
    Icon: Instagram,
    iconColor: 'text-black',
    bg: 'bg-gray-100',
    border: 'border-gray-300',
    activeBg: 'bg-black',
    activeText: 'text-white',
    pillBg: 'bg-gray-100',
    pillText: 'text-black',
    pillHover: 'hover:bg-gray-200',
    accentShadow: 'shadow-gray-200',
  },
  // whatsapp: { key: 'whatsapp', label: 'WhatsApp', Icon: ..., ... },
  // messenger: { key: 'messenger', label: 'Messenger', Icon: ..., ... },
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const Avatar = ({ contact, size = 'md' }) => {
  const sizeClasses = { sm: 'w-8 h-8 text-xs', md: 'w-11 h-11 text-sm', lg: 'w-14 h-14 text-base' };
  const profilePic = contact?.metadata?.profile_pic;
  const gradient = getGradient(contact?.platform_user_id || contact?.conversation_id);
  const initials = getInitials(contact);
  return (
    <div className={`relative flex-shrink-0 rounded-full flex items-center justify-center font-bold text-white shadow-sm overflow-hidden ${sizeClasses[size]}`}>
      {profilePic ? (
        <img src={profilePic} alt={initials} className="w-full h-full object-cover"
          onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }} />
      ) : (
        <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br ${gradient}`}>{initials}</div>
      )}
    </div>
  );
};

const PlatformBadge = ({ platform, className = 'w-5 h-5' }) => {
  const cfg = PLATFORMS[platform];
  if (!cfg) return null;
  const { Icon, bg, iconColor } = cfg;
  return (
    <span className={`${className} rounded-full flex items-center justify-center ${bg} border-2 border-white shadow-sm`}>
      <Icon className={`${iconColor}`} style={{ width: '55%', height: '55%' }} />
    </span>
  );
};

const MessageBubble = ({ message }) => {
  const isMe = message.role === 'model' || message.role === 'human_support';
  return (
    <motion.div
      initial={{ opacity: 0, y: 8, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.18 }}
      className={`flex ${isMe ? 'justify-end' : 'justify-start'} mb-1.5`}
    >
      <div className={`max-w-[75%] sm:max-w-[60%] px-3.5 py-2 rounded-2xl text-sm leading-relaxed shadow-sm ${
        isMe
          ? 'bg-gradient-to-br from-gray-700 to-black text-white rounded-br-sm'
          : 'bg-white border border-gray-100 text-gray-800 rounded-bl-sm'
      }`}>
        {message.role === 'model' && <span className="block text-[9px] font-bold uppercase tracking-widest mb-1 opacity-60">AI Agent</span>}
        {message.role === 'human_support' && <span className="block text-[9px] font-bold uppercase tracking-widest mb-1 opacity-60">Human</span>}
        <p>{message.text}</p>
        <div className={`flex items-center gap-1.5 mt-1 ${isMe ? 'justify-end' : 'justify-start'}`}>
          <span className={`text-[10px] ${isMe ? 'text-gray-300' : 'text-gray-400'}`}>{formatMsgTime(message.date_time)}</span>
          {isMe && <CheckCheck className="h-3.5 w-3.5 text-sky-200" />}
        </div>
      </div>
    </motion.div>
  );
};

const ConversationCard = ({ conv, isActive, onClick }) => {
  const contact = conv.contact || {};
  const displayName = contact?.metadata?.name || contact?.metadata?.username || contact?.platform_user_id || 'Unknown';
  const lastMsg = conv.last_message?.text || '';
  const platform = conv._platform || 'instagram';
  const accountUsername = conv._accountUsername || '';
  const cfg = PLATFORMS[platform];

  return (
    <button
      onClick={onClick}
      className={`w-full text-left flex items-start gap-3 px-4 py-3.5 border-b border-gray-100 transition-all duration-150 ${
        isActive
          ? 'bg-gradient-to-r from-gray-50 to-gray-100 border-l-[3px] border-l-pink-500'
          : 'hover:bg-gray-50 border-l-[3px] border-l-transparent'
      }`}
    >
      {/* Avatar + platform badge */}
      <div className="relative flex-shrink-0">
        <Avatar contact={contact} size="md" />
        <PlatformBadge platform={platform} className="absolute -bottom-0.5 -right-0.5 w-5 h-5" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-sm font-bold text-gray-900 truncate">{displayName}</span>
          <span className="text-[10px] text-gray-400 flex-shrink-0 font-medium">
            {formatTime(conv.last_message?.date_time || conv.updated_at)}
          </span>
        </div>
        {accountUsername && (
          <div className={`inline-flex items-center gap-1 text-[10px] font-semibold ${cfg?.iconColor || 'text-gray-500'} mb-0.5`}>
            {cfg && <cfg.Icon className="w-2.5 h-2.5" />}
            <span>@{accountUsername}</span>
          </div>
        )}
        <p className="text-xs text-gray-500 truncate leading-relaxed">{lastMsg || 'No messages yet'}</p>
      </div>
    </button>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const ConversationsPage = () => {
  const navigate = useNavigate();

  // Accounts loaded from all platforms
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(true);

  // Conversation list
  const [conversations, setConversations] = useState([]);
  const [convLoading, setConvLoading] = useState(false);
  const [convError, setConvError] = useState(null);

  // Filters
  const [platformFilter, setPlatformFilter] = useState('all');
  const [accountFilter, setAccountFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const searchTimer = useRef(null);

  // Active chat
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [convMetadata, setConvMetadata] = useState(null);
  const [msgLoading, setMsgLoading] = useState(false);
  const [msgError, setMsgError] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const chatBottomRef = useRef(null);

  // Mobile: show chat panel
  const [showChat, setShowChat] = useState(false);

  // ── Load accounts ─────────────────────────────────────────────────────────
  const loadAccounts = useCallback(async () => {
    setAccountsLoading(true);
    try {
      const result = await ApiService.getInstagramStatus();
      if (result.success && result.accounts && result.accounts.length > 0) {
        const normalized = result.accounts.map(acc => ({
          platform: 'instagram',
          integrationId: acc.integration_id || acc.id,
          username: acc.account?.instagram_username || acc.username || acc.integration_id,
          displayName: acc.account?.name || acc.account?.instagram_username || 'Instagram Account',
          status: acc.overall_status || 'connected',
        }));
        setAccounts(normalized);
      } else {
        setAccounts([]);
      }
    } catch (err) {
      console.error('Failed to load accounts:', err);
      setAccounts([]);
    } finally {
      setAccountsLoading(false);
    }
  }, []);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  // ── Load conversations ────────────────────────────────────────────────────
  const loadConversations = useCallback(async () => {
    if (accounts.length === 0) return;
    setConvLoading(true);
    setConvError(null);
    try {
      const targetAccounts = accountFilter === 'all'
        ? accounts.filter(a => platformFilter === 'all' || a.platform === platformFilter)
        : accounts.filter(a => a.integrationId === accountFilter);

      const results = await Promise.allSettled(
        targetAccounts.map(acc =>
          ApiService.getInstagramConversations(acc.integrationId, { page: 1, perPage: 30, search: searchQuery })
            .then(res => ({ acc, data: res }))
        )
      );

      const allConvs = [];
      results.forEach(r => {
        if (r.status === 'fulfilled') {
          const { acc, data } = r.value;
          const convList = data?.conversations || data?.data || [];
          convList.forEach(conv => {
            allConvs.push({
              ...conv,
              _platform: acc.platform,
              _integrationId: acc.integrationId,
              _accountUsername: acc.username,
              _accountDisplayName: acc.displayName,
            });
          });
        }
      });

      // Sort by most recent first
      allConvs.sort((a, b) => {
        const aDate = parseDateAsUTC(a.last_message?.date_time || a.updated_at) || 0;
        const bDate = parseDateAsUTC(b.last_message?.date_time || b.updated_at) || 0;
        return bDate - aDate;
      });

      setConversations(allConvs);
    } catch (err) {
      setConvError('Failed to load conversations. Please try again.');
      console.error(err);
    } finally {
      setConvLoading(false);
    }
  }, [accounts, platformFilter, accountFilter, searchQuery]);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  // ── Load messages ─────────────────────────────────────────────────────────
  const loadMessages = useCallback(async (conv) => {
    if (!conv) return;
    setMsgLoading(true);
    setMsgError(null);
    setMessages([]);
    setConvMetadata(null);
    try {
      const res = await ApiService.getInstagramMessages(conv._integrationId, conv.conversation_id, { page: 1, perPage: 30 });
      setMessages(res?.messages || res?.data || []);
      setConvMetadata(res?.conversation_metadata || null);
    } catch (err) {
      setMsgError('Could not load messages.');
    } finally {
      setMsgLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeConv) {
      loadMessages(activeConv);
      if (window.innerWidth < 768) setShowChat(true);
    }
  }, [activeConv, loadMessages]);

  useEffect(() => {
    if (chatBottomRef.current && !msgLoading) {
      chatBottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, msgLoading]);

  // ── Send ──────────────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!replyText.trim() || !activeConv || sending) return;
    const text = replyText.trim();
    setReplyText('');
    setSending(true);
    const tempId = `temp_${Date.now()}`;
    setMessages(prev => [...prev, {
      message_id: tempId, role: 'human_support', text, date_time: new Date().toISOString(), _pending: true,
    }]);
    try {
      await ApiService.sendInstagramDM(activeConv._integrationId, {
        conversation_id: activeConv.conversation_id,
        message: text,
      });
      await loadMessages(activeConv);
    } catch {
      toast.error('Failed to send message');
      setMessages(prev => prev.filter(m => m.message_id !== tempId));
      setReplyText(text);
    } finally {
      setSending(false);
    }
  };

  // ── Search debounce ───────────────────────────────────────────────────────
  const handleSearchChange = (val) => {
    setSearchInput(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setSearchQuery(val), 400);
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const filteredAccounts = platformFilter === 'all'
    ? accounts
    : accounts.filter(a => a.platform === platformFilter);

  const getDisplayName = (conv) => {
    const c = conv?.contact || {};
    return c?.metadata?.name || c?.metadata?.username || c?.platform_user_id || 'Unknown';
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, msg) => {
    const label = getMessageDateLabel(msg.date_time) || 'Messages';
    if (!groups[label]) groups[label] = [];
    groups[label].push(msg);
    return groups;
  }, {});

  const activeAccount = activeConv ? accounts.find(a => a.integrationId === activeConv._integrationId) : null;
  const isDisconnected = activeAccount?.status === 'disconnected' || activeAccount?.status === 'token_expired';

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex h-[calc(100vh-20px)] md:h-[calc(100vh-100px)] bg-white md:rounded-2xl shadow-xl overflow-hidden border border-gray-200 relative">

      {/* ═══ LEFT PANEL: List ══════════════════════════════════════════════ */}
      <div className={`
        flex flex-col bg-white border-r border-gray-200 flex-shrink-0
        w-full md:w-80 lg:w-96
        ${showChat ? 'hidden md:flex' : 'flex'}
      `}>

        {/* Header */}
        <div className="px-4 pt-4 pb-3 border-b border-gray-100 sticky top-0 z-10 bg-white">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Conversations</h1>
              <p className="text-xs text-gray-400 font-medium mt-0.5">
                {convLoading ? 'Loading…' : `${conversations.length} total`}
              </p>
            </div>
            <button
              onClick={loadConversations}
              disabled={convLoading}
              title="Refresh"
              className={`p-2 rounded-xl hover:bg-gray-100 text-gray-500 transition-all ${convLoading ? 'animate-spin' : ''}`}
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>

          {/* Search */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              value={searchInput}
              onChange={e => handleSearchChange(e.target.value)}
              placeholder="Search conversations…"
              className="w-full pl-9 pr-9 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-gray-400 transition-all"
            />
            {searchInput && (
              <button
                onClick={() => { setSearchInput(''); setSearchQuery(''); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Platform pills */}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => { setPlatformFilter('all'); setAccountFilter('all'); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                platformFilter === 'all'
                  ? 'bg-gray-900 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <MessageSquare className="w-3 h-3" />
              All
            </button>

            {Object.values(PLATFORMS).map(p => {
              const count = accounts.filter(a => a.platform === p.key).length;
              if (count === 0) return null;
              const { Icon } = p;
              return (
                <button
                  key={p.key}
                  onClick={() => { setPlatformFilter(p.key); setAccountFilter('all'); }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                    platformFilter === p.key
                      ? `${p.activeBg} ${p.activeText} shadow-sm`
                      : `${p.pillBg} ${p.pillText} ${p.pillHover}`
                  }`}
                >
                  <Icon className="w-3 h-3" />
                  {p.label}
                  <span className="ml-0.5 text-[10px] font-black opacity-60">{count}</span>
                </button>
              );
            })}
          </div>

          {/* Account filter (when multiple accounts of same platform) */}
          {platformFilter !== 'all' && filteredAccounts.length > 1 && (
            <div className="mt-2.5 flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
              <button
                onClick={() => setAccountFilter('all')}
                className={`flex-shrink-0 px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-all ${
                  accountFilter === 'all' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All accounts
              </button>
              {filteredAccounts.map(acc => {
                const cfg = PLATFORMS[acc.platform];
                return (
                  <button
                    key={acc.integrationId}
                    onClick={() => setAccountFilter(acc.integrationId)}
                    className={`flex-shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-all ${
                      accountFilter === acc.integrationId
                        ? `${cfg?.activeBg || 'bg-gray-700'} ${cfg?.activeText || 'text-white'}`
                        : `${cfg?.pillBg || 'bg-gray-50'} ${cfg?.pillText || 'text-gray-600'} ${cfg?.pillHover || 'hover:bg-gray-100'}`
                    }`}
                  >
                    {cfg && <cfg.Icon className="w-3 h-3" />}
                    @{acc.username}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* List body */}
        <div className="flex-1 overflow-y-auto">
          {accountsLoading ? (
            <div className="flex flex-col items-center justify-center h-48 gap-3">
              <Loader2 className="w-7 h-7 animate-spin text-black" />
              <p className="text-sm text-gray-400 font-medium">Loading connected accounts…</p>
            </div>
          ) : accounts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 px-6 text-center">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center mb-4">
                <Instagram className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-base font-bold text-gray-800 mb-1">No integrations connected</h3>
              <p className="text-sm text-gray-500 mb-4">Connect Instagram or other platforms to see conversations here.</p>
              <button
                onClick={() => navigate('/integrations')}
                className="flex items-center gap-2 px-5 py-2.5 bg-black text-white rounded-xl font-semibold text-sm hover:bg-gray-800 transition-colors shadow-md shadow-gray-200"
              >
                <ExternalLink className="w-4 h-4" />
                Go to Integrations
              </button>
            </div>
          ) : convLoading ? (
            <div className="flex flex-col items-center justify-center h-48 gap-3">
              <Loader2 className="w-7 h-7 animate-spin text-black" />
              <p className="text-sm text-gray-400 font-medium">Loading conversations…</p>
            </div>
          ) : convError ? (
            <div className="p-4">
              <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-100 rounded-xl">
                <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                <p className="text-xs text-red-600 font-medium flex-1">{convError}</p>
                <button onClick={loadConversations} className="text-xs text-red-600 font-bold hover:underline">Retry</button>
              </div>
            </div>
          ) : conversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 px-6 text-center">
              <MessageSquare className="w-10 h-10 text-gray-300 mb-3" />
              <h3 className="text-sm font-bold text-gray-700 mb-1">No conversations found</h3>
              <p className="text-xs text-gray-400">Try adjusting your filters or search.</p>
            </div>
          ) : (
            <AnimatePresence>
              {conversations.map(conv => (
                <motion.div
                  key={`${conv._integrationId}_${conv.conversation_id}`}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <ConversationCard
                    conv={conv}
                    isActive={
                      activeConv?.conversation_id === conv.conversation_id &&
                      activeConv?._integrationId === conv._integrationId
                    }
                    onClick={() => setActiveConv(conv)}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          )}
        </div>
      </div>

      {/* ═══ RIGHT PANEL: Chat ════════════════════════════════════════════ */}
      <div className={`
        flex-1 flex flex-col bg-gray-50 min-w-0
        ${showChat ? 'flex' : 'hidden md:flex'}
      `}>
        {activeConv ? (
          <>
            {/* Chat header */}
            <div className="sticky top-0 z-10 bg-white border-b border-gray-200 shadow-sm px-4 py-3 flex items-center gap-3">
              {/* Mobile back */}
              <button
                onClick={() => setShowChat(false)}
                className="md:hidden p-2 rounded-xl hover:bg-gray-100 text-gray-600 transition"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>

              {/* Avatar + badge */}
              <div className="relative flex-shrink-0">
                <Avatar contact={activeConv.contact} size="md" />
                <PlatformBadge platform={activeConv._platform} className="absolute -bottom-0.5 -right-0.5 w-5 h-5" />
              </div>

              {/* Name + account */}
              <div className="flex-1 min-w-0">
                <h2 className="text-sm font-bold text-gray-900 truncate">{getDisplayName(activeConv)}</h2>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  {(() => {
                    const cfg = PLATFORMS[activeConv._platform];
                    if (!cfg) return null;
                    return (
                      <span className={`flex items-center gap-1 text-[11px] font-semibold ${cfg.iconColor}`}>
                        <cfg.Icon className="w-3 h-3" />
                        {cfg.label}
                      </span>
                    );
                  })()}
                  {activeConv._accountUsername && (
                    <span className="text-[11px] text-gray-400 font-medium truncate">
                      via @{activeConv._accountUsername}
                    </span>
                  )}
                </div>
              </div>

              {/* Open in integration detail */}
              <button
                onClick={() => navigate(`/integrations/instagram/${activeConv._integrationId}?tab=inbox&conversation_id=${activeConv.conversation_id}`)}
                title="Open in Integrations"
                className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 border border-gray-300 text-black rounded-xl text-xs font-semibold hover:bg-gray-200 transition-all flex-shrink-0"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Open in Integrations</span>
              </button>
            </div>

            {/* Messages area */}
            <div className="flex-1 overflow-y-auto px-4 py-4">
              {msgLoading ? (
                <div className="flex flex-col items-center justify-center h-full gap-3">
                  <Loader2 className="w-7 h-7 animate-spin text-black" />
                  <p className="text-sm text-gray-400">Loading messages…</p>
                </div>
              ) : msgError ? (
                <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-100 rounded-xl max-w-md mx-auto mt-8">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <p className="text-xs text-red-600 font-medium flex-1">{msgError}</p>
                  <button onClick={() => loadMessages(activeConv)} className="text-xs text-red-600 font-bold">Retry</button>
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <MessageSquare className="w-10 h-10 text-gray-200 mb-3" />
                  <p className="text-sm font-medium text-gray-400">No messages yet</p>
                </div>
              ) : (
                Object.entries(groupedMessages).map(([label, msgs]) => (
                  <div key={label}>
                    <div className="flex items-center gap-3 my-4">
                      <div className="flex-1 h-px bg-gray-200" />
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider px-2">{label}</span>
                      <div className="flex-1 h-px bg-gray-200" />
                    </div>
                    {msgs.map(msg => <MessageBubble key={msg.message_id} message={msg} />)}
                  </div>
                ))
              )}
              <div ref={chatBottomRef} />
            </div>

            {/* Reply box */}
            <div className="bg-white border-t border-gray-200 px-4 py-3">
              {isDisconnected ? (
                <div className="flex items-center gap-3 px-4 py-3.5 bg-red-50 border border-red-100 rounded-2xl text-red-800 justify-center">
                  <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                  <span className="text-xs font-bold text-center">
                    Your account is disconnected or token expired, please reconnect again.
                  </span>
                </div>
              ) : convMetadata && convMetadata.can_reply === false ? (
                <div className="flex items-center gap-3 px-4 py-3.5 bg-amber-50 border border-amber-100 rounded-2xl text-amber-800 justify-center">
                  <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0" />
                  <span className="text-xs font-bold text-center">
                    It's been 24 hours the user messaged you now you can not reply
                  </span>
                </div>
              ) : (
                <div className="flex items-end gap-3">
                  <textarea
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                    placeholder="Type a message… (Enter to send)"
                    rows={2}
                    className="flex-1 resize-none text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-gray-400 transition-all"
                  />
                  <button
                    onClick={handleSend}
                    disabled={!replyText.trim() || sending}
                    className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center bg-black text-white hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-md shadow-gray-200"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          /* Empty state */
          <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
            <div className="relative mb-6">
              <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center shadow-lg">
                <MessageSquare className="w-10 h-10 text-black" />
              </div>
              <div className="absolute -top-1 -right-1 w-8 h-8 rounded-xl bg-gradient-to-br from-gray-800 to-black flex items-center justify-center shadow-md">
                <Instagram className="w-4 h-4 text-white" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Your Conversations Hub</h2>
            <p className="text-sm text-gray-500 max-w-xs leading-relaxed mb-1">
              Select a conversation from the left to view messages and reply.
            </p>
            <p className="text-xs text-gray-400">
              Supports Instagram · WhatsApp · Messenger <span className="italic">(coming soon)</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationsPage;
